package org.gymly.audit

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import kotlinx.coroutines.launch
import org.gymly.data.AdminRepo
import org.gymly.model.AdminRoute
import org.gymly.model.AdminStaffAuditEventType
import org.gymly.model.AdminStaffAuditRecord
import org.gymly.navigation.webSlug
import kotlin.random.Random
import kotlin.time.Clock

private fun newAuditSessionId(): String =
    buildString {
        repeat(36) { idx ->
            when (idx) {
                8, 13, 18, 23 -> append('-')
                else -> append("0123456789abcdef"[Random.nextInt(16)])
            }
        }
    }

/**
 * Registra navegación y cierre de sesión del personal en el panel admin.
 * El inicio de sesión se registra en el servidor al hacer login.
 */
@Composable
fun TrackAdminAudit(
    route: AdminRoute,
    onLogout: () -> Unit,
    content: @Composable (wrappedLogout: () -> Unit) -> Unit,
) {
    val scope = rememberCoroutineScope()
    val sessionId = remember { newAuditSessionId() }
    val sessionStart = remember { Clock.System.now() }
    var previousRoute by remember { mutableStateOf<AdminRoute?>(null) }
    var moduleEnteredAt by remember { mutableStateOf(Clock.System.now()) }

    fun recordAsync(block: suspend () -> Unit) {
        scope.launch {
            runCatching { block() }
        }
    }

    LaunchedEffect(route) {
        val now = Clock.System.now()
        previousRoute?.let { prev ->
            val seconds = (now - moduleEnteredAt).inWholeSeconds.coerceAtLeast(0)
            recordAsync {
                AdminRepo.api.recordStaffAuditEvent(
                    AdminStaffAuditRecord(
                        eventType = AdminStaffAuditEventType.MODULE_VIEW,
                        moduleCode = prev.webSlug(),
                        moduleLabel = prev.label,
                        detail = "Tiempo en módulo",
                        sessionId = sessionId,
                        durationSeconds = seconds,
                    ),
                )
            }
        }
        previousRoute = route
        moduleEnteredAt = now
    }

    val wrappedLogout: () -> Unit = {
        val totalSeconds = (Clock.System.now() - sessionStart).inWholeSeconds.coerceAtLeast(0)
        recordAsync {
            previousRoute?.let { prev ->
                val moduleSeconds = (Clock.System.now() - moduleEnteredAt).inWholeSeconds.coerceAtLeast(0)
                AdminRepo.api.recordStaffAuditEvent(
                    AdminStaffAuditRecord(
                        eventType = AdminStaffAuditEventType.MODULE_VIEW,
                        moduleCode = prev.webSlug(),
                        moduleLabel = prev.label,
                        detail = "Salida del módulo",
                        sessionId = sessionId,
                        durationSeconds = moduleSeconds,
                    ),
                )
            }
            AdminRepo.api.recordStaffAuditEvent(
                AdminStaffAuditRecord(
                    eventType = AdminStaffAuditEventType.LOGOUT,
                    detail = "Cierre de sesión",
                    sessionId = sessionId,
                    durationSeconds = totalSeconds,
                ),
            )
        }
        onLogout()
    }

    content(wrappedLogout)
}
